import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import type { CrossReferenceLookupRunRecordModel } from '../dto/cross-reference-lookup-review.dto';
import type { CrossReferenceRescoreRunRecordModel } from '../dto/cross-reference-rescore.dto';
import { BaseEntity } from './base.entity';
import { ClientEntity } from './client.entity';
import type { CrossReferenceRequestLineEntity } from './cross-reference-request-line.entity';

/** How the request arrived. */
export type CrossReferenceRequestSource = 'email' | 'manual' | 'api';

/**
 * Lifecycle of the whole request.
 *
 * `completed` means a human has finished with it (normally by drafting the
 * quote); it is NOT an engine verdict. The engine only ever writes the
 * per-line summary fields and the rollups.
 *
 * `dismissed` means a reviewer judged the request irrelevant — spam, a duplicate
 * document, an enquiry that is not a quote request. It is deliberately a status
 * rather than a soft delete: the request keeps every line, candidate and piece
 * of provenance it had, because "we looked at this and decided not to quote it"
 * is itself the answer somebody may need later. The status a dismissal
 * interrupted is kept in `statusBeforeDismiss` so undismissing restores it.
 */
export type CrossReferenceRequestStatus =
	| 'pending_review'
	| 'completed'
	| 'cancelled'
	| 'dismissed';

/**
 * One customer cross-reference request: a document (email, upload, API post) of
 * external article lines to be matched against our catalog.
 *
 * The container is a NEW entity set rather than an extension of
 * `sourcing_request` because the two run in opposite directions — sourcing is
 * procurement (we need to buy something), cross-reference is sales (a customer
 * asked what we sell that is equivalent). Overloading one table would put two
 * unrelated lifecycles behind one status column.
 */
@Index('idx_crr_status', ['status'])
@Index('idx_crr_client', ['fkClientId'])
@Index('idx_crr_email_record', ['fkEmailRecordId'])
@Index('idx_crr_automation_execution', ['fkAutomationExecutionId'])
@Index('idx_crr_email_source', ['fkEmailSourceId'])
@Index('idx_crr_intake', ['fkCrossReferenceIntakeId'])
@Entity('cross_reference_request')
export class CrossReferenceRequestEntity extends BaseEntity {
	/**
	 * Nullable because intake can outrun identification: an inbound email whose
	 * sender resolves to zero or several clients is a legitimate, reviewable
	 * state. The review UI is where a human picks the client; nothing downstream
	 * may guess one.
	 */
	@Column('int', { name: 'fk_client__id', nullable: true, unsigned: true })
	public fkClientId!: number | null;

	@Column('int', {
		name: 'fk_company__id',
		unsigned: true,
		default: () => "'1'",
	})
	public fkCompanyId!: number;

	/** Provenance back to the inbound email this request was extracted from. */
	@Column('int', {
		name: 'fk_email_record__id',
		nullable: true,
		unsigned: true,
	})
	public fkEmailRecordId!: number | null;

	/** Set once a draft quote has been created from this request. */
	@Column('int', { name: 'fk_order__id', nullable: true, unsigned: true })
	public fkOrderId!: number | null;

	// ── Intake provenance (all nullable; a manual or API request has none) ────

	/** Which run produced this request. Null for manual and API intake. */
	@Column('int', {
		name: 'fk__automation_execution__id',
		nullable: true,
		unsigned: true,
	})
	public fkAutomationExecutionId!: number | null;

	/** Which mailbox the email arrived in. */
	@Column('int', {
		name: 'fk_email_source__id',
		nullable: true,
		unsigned: true,
	})
	public fkEmailSourceId!: number | null;

	/** Which managed intake's declarations the run was compiled from. */
	@Column('int', {
		name: 'fk_cross_reference_intake__id',
		nullable: true,
		unsigned: true,
	})
	public fkCrossReferenceIntakeId!: number | null;

	/**
	 * What was attached to the inbound email, by filename.
	 *
	 * Denormalized so the queue can say what the reader worked from without
	 * re-opening the email, and so it survives the email being archived.
	 */
	@Column('json', { name: 'attachmentNames', nullable: true })
	public attachmentNames!: Array<string> | null;

	/** When the quote was drafted, for time-to-quote reporting. */
	@Column('datetime', { name: 'quotedAt', nullable: true })
	public quotedAt!: Date | null;

	/** Display name — typically the email subject, or "Manual request". */
	@Column('varchar', { name: 'name', length: 255 })
	public name!: string;

	@Column('varchar', { name: 'source', length: 32 })
	public source!: CrossReferenceRequestSource;

	/**
	 * Denormalized from the inbound email so the queue list can show who asked
	 * without joining `email_record` (and so it survives the email being
	 * archived or the client later being resolved).
	 */
	@Column('varchar', { name: 'senderEmail', nullable: true, length: 255 })
	public senderEmail!: string | null;

	@Column('varchar', { name: 'status', length: 32, default: 'pending_review' })
	public status!: CrossReferenceRequestStatus;

	// Denormalized rollups maintained by the persistence service on every match
	// run (the `sourcing_request` precedent). They exist so the request queue can
	// render counts without a correlated aggregate over the line table per row.
	@Column('int', { name: 'lineCount', unsigned: true, default: 0 })
	public lineCount!: number;

	@Column('int', { name: 'autoEligibleCount', unsigned: true, default: 0 })
	public autoEligibleCount!: number;

	@Column('int', { name: 'needsReviewCount', unsigned: true, default: 0 })
	public needsReviewCount!: number;

	@Column('int', { name: 'noMatchCount', unsigned: true, default: 0 })
	public noMatchCount!: number;

	@Column('int', { name: 'confirmedCount', unsigned: true, default: 0 })
	public confirmedCount!: number;

	/** When the matching engine last ran over this request's lines. */
	@Column('datetime', { name: 'matchedAt', nullable: true })
	public matchedAt!: Date | null;

	/** Matching category the run was scored under, for reproducibility. */
	@Column('varchar', { name: 'category', nullable: true, length: 64 })
	public category!: string | null;

	// ── Dismissal (reviewer judgement that the request is not worth quoting) ──

	@Column('datetime', { name: 'dismissedAt', nullable: true })
	public dismissedAt!: Date | null;

	@Column('int', { name: 'dismissedByUserId', nullable: true, unsigned: true })
	public dismissedByUserId!: number | null;

	@Column('varchar', { name: 'dismissReason', nullable: true, length: 500 })
	public dismissReason!: string | null;

	/**
	 * The status the request held when it was dismissed.
	 *
	 * Recorded rather than inferred: a dismissal can interrupt a request that was
	 * still under review OR one already completed, and guessing between them on
	 * undismiss (say, from the presence of a drafted order) would silently reopen
	 * finished work the first time the heuristic was wrong.
	 */
	@Column('varchar', {
		name: 'statusBeforeDismiss',
		nullable: true,
		length: 32,
	})
	public statusBeforeDismiss!: CrossReferenceRequestStatus | null;

	// ── Reviewer-triggered external lookup ───────────────────────────────────

	/**
	 * The last manual (reviewer-triggered) external lookup on this request.
	 *
	 * ONE slot, deliberately overwritten by each run rather than appended to: it
	 * exists to answer "is a run happening right now, and how did the last one
	 * end". What a run actually produced lives on the LINES it enriched, and what
	 * it cost lives in the LLM usage ledger; a history table here would duplicate
	 * both without answering anything the screen asks.
	 *
	 * A `running` value is also the in-flight lock — a second manual run for the
	 * same request is refused while one stands — which is why every completion
	 * path has to write a terminal status.
	 *
	 * Typed as the internal RECORD, not the served DTO: the row keeps the run's
	 * dollar rails for reconciliation against the usage ledger, and the review
	 * endpoints strip them on the way out.
	 */
	@Column('json', { name: 'lookupRun', nullable: true })
	public lookupRun!: CrossReferenceLookupRunRecordModel | null;

	// ── Staleness (the scores no longer reflect the current rules) ───────────

	/**
	 * When something the engine reads changed after this request was scored.
	 *
	 * A MARK, never a trigger: linking a name or editing the matching rules
	 * rewrites what every future comparison reads, and re-scoring every open
	 * request inside the HTTP request that made the change took minutes on a
	 * real catalog. So the change stamps the requests it could have moved, and a
	 * person decides when to spend the re-score. Cleared by the re-score itself.
	 */
	@Column('datetime', { name: 'staleAt', nullable: true })
	public staleAt!: Date | null;

	/**
	 * What changed, in the words the screen shows the reviewer.
	 *
	 * Overwritten rather than appended to: the newest change is the one a person
	 * needs to read, and a growing list of every alias edit since the last
	 * re-score would say nothing the re-score does not already settle.
	 */
	@Column('varchar', { name: 'staleReason', nullable: true, length: 255 })
	public staleReason!: string | null;

	/**
	 * The last manual re-score of this request.
	 *
	 * ONE slot with the same contract as `lookupRun` above: it answers "is a
	 * re-score happening right now, and how did the last one end", and a
	 * `queued`/`running` value is the lock a second request reads. What the
	 * re-score produced lives on the LINES it re-scored.
	 */
	@Column('json', { name: 'rescoreRun', nullable: true })
	public rescoreRun!: CrossReferenceRescoreRunRecordModel | null;

	@ManyToOne(() => ClientEntity, {
		onDelete: 'SET NULL',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_client__id', referencedColumnName: 'id' }])
	public fkClient!: ClientEntity | null;

	// String-based reference: the line entity imports this one for its own
	// ManyToOne, so a value import here would be a runtime cycle.
	@OneToMany('CrossReferenceRequestLineEntity', 'fkCrossReferenceRequest')
	public lines!: Array<CrossReferenceRequestLineEntity>;
}
